import { Layers, Minus, Package, Plus } from "lucide-react";
import { useState } from "react";
import type { ProductVariant, TProductWithRelation } from "@/types/product-type";
import { usePosContext } from "../context/pos-context";

type PosProductCardProps = {
  productWithRelation: TProductWithRelation;
  /** When provided (in "variants" display mode), renders this specific variant directly as a card */
  forcedVariant?: ProductVariant;
};

const MAX_INLINE_PILLS = 4;

/**
 * Extracts a short, human-friendly option label for a variant (e.g., "Red / M" or "XL").
 */
function getVariantShortLabel(variant: ProductVariant, productName: string): string {
  if (variant.variantKey) {
    try {
      const parsed = JSON.parse(variant.variantKey);
      if (parsed && typeof parsed === "object") {
        const vals = Object.values(parsed)
          .map((v) => String(v ?? "").trim())
          .filter(Boolean);
        if (vals.length > 0) {
          return vals.join(" / ");
        }
      }
    } catch {
      // Fall back to variant name cleanup
    }
  }

  const rawName = (variant.name ?? "").trim();
  if (!rawName) return variant.sku || "Default";

  // Strip prefix if variant name starts with product name (e.g. "T-Shirt - Red - M" -> "Red - M")
  const cleanProduct = productName.trim();
  if (
    cleanProduct &&
    rawName.toLowerCase().startsWith(cleanProduct.toLowerCase()) &&
    rawName.length > cleanProduct.length
  ) {
    const stripped = rawName
      .slice(cleanProduct.length)
      .replace(/^[\s\-–—:/|]+/, "")
      .trim();
    if (stripped) return stripped;
  }

  return rawName;
}

export function PosProductCard({
  productWithRelation,
  forcedVariant,
}: PosProductCardProps) {
  const { cart, handleOpenProductDetail } = usePosContext();
  const { product, productVariants } = productWithRelation;
  const variants = productVariants ?? [];

  // In "products" mode with multiple variants, allow selecting a variant inline on the card
  const [selectedVariantId, setSelectedVariantId] = useState<string>(() =>
    String(forcedVariant?.id ?? variants[0]?.id ?? product.id)
  );

  const isSingleVariantOrForced = Boolean(forcedVariant) || variants.length <= 1;
  const activeVariant: ProductVariant | undefined =
    forcedVariant ??
    variants.find((v) => String(v.id) === selectedVariantId) ??
    variants[0];

  const activeVariantId = String(activeVariant?.id ?? product.id);
  const price = activeVariant?.price ?? product.price ?? 0;
  const imageUrl = activeVariant?.imageUrl || product.imageUrl;
  const cartItemName = activeVariant?.name || product.name;
  const displayPrice = typeof price === "number" ? price.toFixed(2) : "0.00";

  // Quantity of the currently active variant in cart
  const activeVariantQty = cart.getQuantity(activeVariantId);

  // Total quantity across all variants of this product (for badge on product image)
  const totalProductQtyInCart = forcedVariant
    ? activeVariantQty
    : variants.length > 1
      ? variants.reduce((sum, v) => sum + cart.getQuantity(String(v.id)), 0)
      : activeVariantQty;

  const variantShortLabel = activeVariant
    ? getVariantShortLabel(activeVariant, product.name)
    : null;
  const showDistinctVariantSubtitle =
    Boolean(forcedVariant) &&
    Boolean(variantShortLabel) &&
    variantShortLabel?.toLowerCase() !== product.name.trim().toLowerCase() &&
    variantShortLabel?.toLowerCase() !== "default";

  const handleIncreaseActive = (e: React.MouseEvent) => {
    e.stopPropagation();
    cart.addItem({
      productId: String(product.id),
      variantId: activeVariantId,
      name: cartItemName,
      imageUrl: imageUrl ?? null,
      price: typeof price === "number" ? price : 0,
    });
  };

  const handleDecreaseActive = (e: React.MouseEvent) => {
    e.stopPropagation();
    cart.updateQty(activeVariantId, activeVariantQty - 1);
  };

  const handleImageOrTitleClick = () => {
    if (!isSingleVariantOrForced && variants.length > 1) {
      handleOpenProductDetail(productWithRelation);
    } else {
      cart.addItem({
        productId: String(product.id),
        variantId: activeVariantId,
        name: cartItemName,
        imageUrl: imageUrl ?? null,
        price: typeof price === "number" ? price : 0,
      });
    }
  };

  const inlineVariants = variants.slice(0, MAX_INLINE_PILLS);
  const remainingVariantCount = Math.max(0, variants.length - MAX_INLINE_PILLS);

  return (
    <div className="w-full rounded-xl overflow-hidden bg-white border border-gray-200/90 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between">
      <div>
        {/* Product / Variant Image */}
        <button
          type="button"
          onClick={handleImageOrTitleClick}
          className="relative aspect-[4/3] bg-gray-50 w-full group block overflow-hidden"
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={cartItemName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <Package className="h-10 w-10 md:h-12 md:w-12 text-gray-400" />
            </div>
          )}

          {/* Multi-variant count badge on top-left when in Product View */}
          {!isSingleVariantOrForced && variants.length > 1 && (
            <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/65 backdrop-blur-sm text-white text-[10px] font-medium">
              <Layers className="h-2.5 w-2.5" />
              {variants.length} options
            </span>
          )}

          {/* Direct Variant SKU / Option pill on top-left when in Variant View */}
          {showDistinctVariantSubtitle && (
            <span className="absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full bg-sky-600/90 backdrop-blur-sm text-white text-[10px] font-semibold shadow-sm max-w-[75%] truncate">
              {variantShortLabel}
            </span>
          )}

          {/* Total quantity badge in top-right */}
          {totalProductQtyInCart > 0 && (
            <span
              className="absolute top-1.5 right-1.5 md:top-2 md:right-2 inline-flex items-center justify-center min-w-[22px] md:min-w-[24px] h-5 md:h-6 px-1.5 md:px-2 rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-md"
              aria-label={`${totalProductQtyInCart} in cart`}
            >
              {totalProductQtyInCart}
            </span>
          )}
        </button>

        {/* Title & Variant Selector Pills */}
        <div className="p-2.5 md:p-3 pb-1.5 space-y-1.5">
          <button
            type="button"
            onClick={handleImageOrTitleClick}
            className="w-full text-left"
          >
            <h3 className="font-semibold text-foreground text-xs md:text-sm leading-snug line-clamp-1">
              {product.name}
            </h3>
            {showDistinctVariantSubtitle && (
              <p className="text-[11px] font-medium text-sky-700 line-clamp-1 mt-0.5">
                {variantShortLabel}
              </p>
            )}
          </button>

          {/* Inline Clickable Variant Pills for Multi-Variant Products */}
          {!isSingleVariantOrForced && variants.length > 1 && (
            <div className="flex items-center flex-wrap gap-1 pt-0.5">
              {inlineVariants.map((v) => {
                const vId = String(v.id);
                const isSelected = vId === activeVariantId;
                const vQty = cart.getQuantity(vId);
                const label = getVariantShortLabel(v, product.name);
                return (
                  <button
                    key={vId}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVariantId(vId);
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] md:text-[11px] font-medium border transition-all max-w-full truncate ${
                      isSelected
                        ? "bg-primary/10 text-primary border-primary font-semibold shadow-2xs"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <span className="truncate max-w-[75px]">{label}</span>
                    {vQty > 0 && (
                      <span className="inline-flex items-center justify-center px-1 min-w-[14px] h-3.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold">
                        {vQty}
                      </span>
                    )}
                  </button>
                );
              })}

              {remainingVariantCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenProductDetail(productWithRelation);
                  }}
                  className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
                >
                  +{remainingVariantCount} more
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row: Price + Direct Quick Add / Stepper */}
      <div className="px-2.5 md:px-3 pb-2.5 md:pb-3 pt-1 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm md:text-base font-bold text-primary leading-none">
            ${displayPrice}
          </p>
        </div>

        {/* Inline Stepper / Add Button for active variant */}
        {activeVariantQty > 0 ? (
          <div
            className="inline-flex items-center gap-1 bg-primary/10 border border-primary/20 rounded-lg p-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleDecreaseActive}
              className="h-6 w-6 md:h-7 md:w-7 rounded-md bg-white text-primary hover:bg-gray-50 flex items-center justify-center shadow-2xs transition-colors"
              aria-label="Decrease quantity"
            >
              <Minus className="h-3 w-3 md:h-3.5 md:w-3.5" />
            </button>
            <span className="min-w-[18px] text-center text-xs font-bold text-primary">
              {activeVariantQty}
            </span>
            <button
              type="button"
              onClick={handleIncreaseActive}
              className="h-6 w-6 md:h-7 md:w-7 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center shadow-2xs transition-colors"
              aria-label="Increase quantity"
            >
              <Plus className="h-3 w-3 md:h-3.5 md:w-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleIncreaseActive}
            className="inline-flex items-center gap-1 h-7 md:h-8 px-2.5 md:px-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold shadow-2xs transition-colors shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add</span>
          </button>
        )}
      </div>
    </div>
  );
}
